import axios from '@/api/axios-config'
import type {
  ChatConversation,
  ChatFileInput,
  ChatImageInput,
  ChatVideoInput,
  ChatMessage,
  ChatMessagePage,
  ChatNotifySettings,
  ChatPresence,
  ChatPresenceBatch,
  ChatRealtimeToken,
  ChatUnread,
  ChatUploadPresign,
  ChatUserRef,
  ConversationKind,
} from '../types'

/**
 * Messages 的 REST 接口（前缀 /system/chat，main-api chat_controller.py）。
 * axios-config 的拦截器已解包，返回值就是响应体。
 *
 * 会话不存在 / 我已不在里面 = 404，是正常业务态（页面自己显示「这个会话不可用」），
 * 所以读会话的请求关掉全局 404 toast。
 */

const BASE = '/system/chat'
const QUIET_404 = { suppressNotFoundToast: true } as Record<string, unknown>

/** 选人列表一次列全（后端上限 1000）：建群 / 加人时不分页。 */
export const CONTACTS_LIMIT = 1000
/** 会话列表一次取多少（后端上限 200） */
export const CONVERSATIONS_LIMIT = 200
export const MESSAGE_PAGE = 50

export interface CreateConversationArgs {
  kind: ConversationKind
  memberIds: string[]
  title?: string
}

export interface SendMessageArgs {
  /** text = 正文；image / video / file = 说明文字（可为空串） */
  body: string
  clientMsgId: string
  kind?: 'text' | 'image' | 'video' | 'file'
  image?: ChatImageInput
  /** kind = video 时必带（第五批 §1.5） */
  video?: ChatVideoInput
  /** kind = file 时必带（第五批 §1.5） */
  file?: ChatFileInput
  /** 群聊里 @ 的人（单聊不能带，后端 400） */
  mentionUserIds?: string[]
  /** 群聊里 @所有人 */
  mentionAll?: boolean
  /** 回复同一会话里的哪一条（第四批；不回复时不带） */
  replyToMessageId?: string
  /** 引用的任务 id（第四批 §5，最多 5 个；没有时不带）。只带任务、正文为空也能发 */
  taskRefIds?: string[]
}

/** PATCH /conversations/{id}/notify：只传要改的。muted 从关到开时，后端把没传的三项重置为关 */
export type NotifyPatch = Partial<Pick<ChatNotifySettings, 'muted' | 'mentionMe' | 'mentionAll'>> & {
  followedUserIds?: string[]
}

/**
 * 发消息的请求体。@ 字段、replyToMessageId 只在真的有时才带：请求 schema 是 extra=forbid，
 * 还没升级的后端收到不认识的字段会 422（新旧后端都能发普通消息）。
 */
export function sendMessageBody({
  body, clientMsgId, kind, image, video, file, mentionUserIds, mentionAll, replyToMessageId, taskRefIds,
}: SendMessageArgs): Record<string, unknown> {
  // 附件块只带与 kind 对应的那一个（后端：别的附件块出现就 422）
  const out: Record<string, unknown> =
    kind === 'image' ? { kind, body, clientMsgId, image }
      : kind === 'video' ? { kind, body, clientMsgId, video }
        : kind === 'file' ? { kind, body, clientMsgId, file }
          : { body, clientMsgId }
  if (mentionUserIds && mentionUserIds.length > 0) out.mentionUserIds = mentionUserIds
  if (mentionAll) out.mentionAll = true
  if (replyToMessageId) out.replyToMessageId = replyToMessageId
  if (taskRefIds && taskRefIds.length > 0) out.refs = taskRefIds.map((id) => ({ type: 'task', id: String(id) }))
  return out
}

/** 建会话 / 加人时对方不在我的可见范围（通讯录按任务关系收窄，第四批 §3.4） */
export const CHAT_CONTACT_NOT_ALLOWED = 'CHAT_CONTACT_NOT_ALLOWED'
/** 回复的消息不是本会话里能回复的（原消息没了 / 是系统消息），400（第四批 §4） */
export const CHAT_REPLY_TARGET_INVALID = 'CHAT_REPLY_TARGET_INVALID'
/** 引用的任务里有发送人看不到的（不存在 / 房源范围外 / 没有任务读权限），400（第四批 §5） */
export const CHAT_REF_INVALID = 'CHAT_REF_INVALID'

/**
 * 失败请求的业务错误码（大写）。typed error（拦截器翻译过的）在 .code；原始 axios error 在响应体的
 * error_code / error（后端 error 是小写）。认不出来返回 undefined。
 */
export function errorCode(err: unknown): string | undefined {
  const e = err as { code?: unknown; response?: { data?: { error_code?: unknown; error?: unknown } } } | null | undefined
  const data = e?.response?.data
  const raw = data?.error_code ?? data?.error ?? e?.code
  // axios 自己的 code（ERR_NETWORK 等）不是业务码，但大写比较不会撞上 CHAT_*，原样返回即可
  return typeof raw === 'string' && raw ? raw.toUpperCase() : undefined
}

/** 这次失败是不是「不能和此人发消息」 */
export function isContactNotAllowed(err: unknown): boolean {
  return errorCode(err) === CHAT_CONTACT_NOT_ALLOWED
}

export interface RequestUploadArgs {
  fileName: string
  mimeType: string
  sizeBytes?: number
}

/** 取出失败请求的 HTTP 状态（typed error 带 .status，原始 axios error 在 .response.status）。 */
export function errorStatus(err: unknown): number | undefined {
  const e = err as { status?: unknown; response?: { status?: unknown } } | null | undefined
  const status = e?.status ?? e?.response?.status
  return typeof status === 'number' ? status : undefined
}

export const chatApi = {
  listConversations: async (): Promise<ChatConversation[]> => {
    const raw = await axios.get<unknown, { items?: ChatConversation[] }>(`${BASE}/conversations`, {
      params: { limit: CONVERSATIONS_LIMIT },
    })
    return raw?.items ?? []
  },

  getConversation: (id: string) =>
    axios.get<unknown, ChatConversation>(`${BASE}/conversations/${id}`, QUIET_404),

  listMessages: (conversationId: string, opts: { before?: string; limit?: number } = {}) =>
    axios.get<unknown, ChatMessagePage>(`${BASE}/conversations/${conversationId}/messages`, {
      ...QUIET_404,
      params: opts.before
        ? { before: opts.before, limit: opts.limit ?? MESSAGE_PAGE }
        : { limit: opts.limit ?? MESSAGE_PAGE },
    }),

  unreadCount: () => axios.get<unknown, ChatUnread>(`${BASE}/unread-count`),

  contacts: async (q?: string): Promise<ChatUserRef[]> => {
    const raw = await axios.get<unknown, { items?: ChatUserRef[] }>(`${BASE}/contacts`, {
      params: q ? { q, limit: CONTACTS_LIMIT } : { limit: CONTACTS_LIMIT },
    })
    return raw?.items ?? []
  },

  realtimeToken: () => axios.get<unknown, ChatRealtimeToken>(`${BASE}/realtime-token`),

  createConversation: (body: CreateConversationArgs) =>
    axios.post<unknown, ChatConversation>(`${BASE}/conversations`, body),

  sendMessage: (conversationId: string, args: SendMessageArgs) =>
    axios.post<unknown, ChatMessage>(
      `${BASE}/conversations/${conversationId}/messages`,
      sendMessageBody(args),
      QUIET_404,
    ),

  markRead: (conversationId: string, messageId?: string) =>
    axios.post<unknown, { conversationId: string; lastReadMessageId?: string | null }>(
      `${BASE}/conversations/${conversationId}/read`,
      messageId ? { messageId } : {},
      QUIET_404,
    ),

  /** 图片上传的预签名 PUT（私有 key，按会话分目录）。 */
  requestUpload: (conversationId: string, body: RequestUploadArgs) =>
    axios.post<unknown, ChatUploadPresign>(`${BASE}/conversations/${conversationId}/uploads`, body, QUIET_404),

  /** 改我自己的通知设置（仅群聊；回包是带新 notify 的会话） */
  updateNotify: (conversationId: string, patch: NotifyPatch) =>
    axios.patch<unknown, ChatConversation>(`${BASE}/conversations/${conversationId}/notify`, patch, QUIET_404),

  /**
   * 对方在不在线。还没上这个接口的后端（404）与查不了（enabled=false）调用方一样当作不显示。
   * 404 不弹全局 toast（每 30 秒查一次，弹了会刷屏）。
   */
  presence: (conversationId: string) =>
    axios.get<unknown, ChatPresence>(`${BASE}/conversations/${conversationId}/presence`, QUIET_404),

  /**
   * 会话列表：一次取一批人的状态（第五批；最多 100 个，调用方先去重截断）。
   * 老后端没有这个接口（404 / 405）由调用方静默处理，404 不弹全局 toast（每 60 秒一次）。
   */
  presenceBatch: (userIds: string[]) =>
    axios.post<unknown, ChatPresenceBatch>(`${BASE}/presence`, { userIds }, QUIET_404),

  /** 改群名（只有群主；其他人 403） */
  rename: (conversationId: string, title: string) =>
    axios.patch<unknown, ChatConversation>(`${BASE}/conversations/${conversationId}`, { title }, QUIET_404),

  /** 加人（任一成员，仅群聊；已在群里的后端忽略） */
  addMembers: (conversationId: string, memberIds: string[]) =>
    axios.post<unknown, ChatConversation>(`${BASE}/conversations/${conversationId}/members`, { memberIds }, QUIET_404),

  /** 群主移出别人；userId = 自己时是退群（群主退群由后端把群主转给入群最早的成员） */
  removeMember: (conversationId: string, userId: string) =>
    axios.delete<unknown, { conversationId: string; userId: string }>(
      `${BASE}/conversations/${conversationId}/members/${userId}`,
      QUIET_404,
    ),
}
